'use client'

import type { Fetchable } from '@morpho-org/blue-sdk'

import { isDefined } from '@morpho-org/morpho-ts'
import { cn } from '@morpho-org/ui-v6/utils'
import { Button } from '@repo/ui'
import { Arrow } from '@repo/ui/icons'
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import { Skeleton } from '../server/Skeleton'

export interface PaginationProps {
  pagination: Fetchable<PaginationState>
  totalItemsCount: Fetchable<number>
  previousPage: () => void
  nextPage: () => void
  setPage?: (page: number) => void
  canPreviousPage: Fetchable<boolean>
  canNextPage: Fetchable<boolean>
  loading: boolean
  error?: Error | null
  onPrefetchNext?: () => void
  onPrefetchPrevious?: () => void
  className?: string
}

export interface PaginationState {
  pageIndex: number
  pageSize: number
}

const pipeIndex = (index?: number) => {
  return index ? index + 1 : 1
}

export function Pagination({
  pagination,
  totalItemsCount,
  previousPage,
  nextPage,
  setPage,
  canPreviousPage,
  canNextPage,
  loading,
  error,
  className,
  onPrefetchNext,
  onPrefetchPrevious
}: PaginationProps) {
  const hasPages = useMemo(() => {
    return (
      isDefined(pagination) && isDefined(totalItemsCount) && totalItemsCount > pagination.pageSize
    )
  }, [pagination, totalItemsCount])

  const [pageCount, setPageCount] = useState<Fetchable<number>>()
  const [pageUserInput, setPageUserInput] = useState<number | string>(
    pipeIndex(pagination?.pageIndex)
  )
  const [widthSkeleton, setWidthSkeleton] = useState<number>(27)
  const elementPaginationRef = useRef<HTMLElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const lastNavigationDirection = useRef<'next' | 'previous' | null>(null)

  const isAllDefined = useMemo(() => {
    return isDefined(pageCount) && isDefined(pagination) && isDefined(totalItemsCount)
  }, [pageCount, pagination, totalItemsCount])

  const isNextBtnDisabled = useMemo(() => {
    return (
      !canNextPage ||
      !isAllDefined ||
      !pageCount ||
      !pagination ||
      pagination.pageIndex + 1 >= pageCount
    )
  }, [canNextPage, isAllDefined, pageCount, pagination])

  const isPrevBtnDisabled = useMemo(() => {
    return !canPreviousPage || !isAllDefined || !pagination || pagination.pageIndex <= 0
  }, [canPreviousPage, isAllDefined, pagination])

  const digitInput = useMemo(() => {
    const length = pageUserInput.toString().length
    if (!length) return 1
    return length
  }, [pageUserInput])

  const debounceCallback = useDebouncedCallback((newPage: number | null) => {
    if (!pageCount) return
    if (!newPage) return
    if (setPage && newPage > 0 && newPage <= pageCount) {
      setPage(newPage - 1)
    }
  }, 400)

  const onPageNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      if (!pageCount) return
      if (!e.target.value) {
        setPageUserInput('')
        debounceCallback(null)
        return
      }

      const newPage = parseInt(e.target.value)

      if (isNaN(newPage)) {
        throw new Error('Invalid page number')
      }

      if (newPage > 0 && newPage <= pageCount) {
        setPageUserInput(newPage)
        debounceCallback(newPage)
      } else {
        throw new Error('Page number out of range')
      }
    } catch (error) {
      console.warn('Warn in onPageNumberChange:', error)
    }
  }

  useEffect(() => {
    if (isDefined(pagination) && isDefined(totalItemsCount)) {
      setPageCount(Math.ceil(totalItemsCount / pagination.pageSize))
      setPageUserInput(pipeIndex(pagination.pageIndex))
    }
  }, [totalItemsCount, pagination])

  useEffect(() => {
    const width = elementPaginationRef.current?.getBoundingClientRect().width
    if (width) setWidthSkeleton(width)
  }, [pageUserInput, pagination])

  // Prefetch next page after navigation click
  // This handles the case where mouseenter doesn't fire because mouse is already over the button
  useEffect(() => {
    if (!lastNavigationDirection.current) return

    if (lastNavigationDirection.current === 'next') {
      onPrefetchNext?.()
    } else {
      onPrefetchPrevious?.()
    }

    lastNavigationDirection.current = null
  }, [pagination?.pageIndex, onPrefetchNext, onPrefetchPrevious])

  if (error) return null

  if (totalItemsCount && (hasPages || (pagination && pagination.pageIndex > 0)))
    return (
      <div className={cn('gap-sm flex flex-row items-center justify-center', className)}>
        <Button
          variant="secondary"
          size="icon"
          onClick={() => {
            lastNavigationDirection.current = 'previous'
            previousPage()
          }}
          disabled={isPrevBtnDisabled}
          onMouseEnter={isPrevBtnDisabled ? undefined : onPrefetchPrevious}
          aria-label="Previous page"
        >
          <Arrow className="rotate-180" />
        </Button>

        <span
          ref={elementPaginationRef}
          className={cn(
            'px-sm py-2xs flex items-center gap-[2.5px] rounded-[20px]',
            hasPages ? 'bg-muted cursor-text' : 'bg-muted cursor-default'
          )}
          onClick={event => {
            if (hasPages) {
              event.stopPropagation()
              inputRef.current?.focus()
            }
          }}
        >
          <input
            ref={inputRef}
            value={pageUserInput}
            onChange={onPageNumberChange}
            onBlur={() => {
              setPageUserInput(pipeIndex(pagination?.pageIndex))
            }}
            readOnly={!hasPages}
            style={{ width: `${digitInput}ch` }}
            className="text-foreground bg-transparent text-center text-[10px] leading-none outline-none"
          />
          <span className="text-foreground text-[10px] leading-none">of {pageCount}</span>
        </span>

        <Button
          variant="secondary"
          size="icon"
          onClick={() => {
            lastNavigationDirection.current = 'next'
            nextPage()
          }}
          disabled={isNextBtnDisabled}
          onMouseEnter={isNextBtnDisabled ? undefined : onPrefetchNext}
          aria-label="Next page"
        >
          <Arrow />
        </Button>
      </div>
    )

  if (loading) return <PaginationSkeleton width={widthSkeleton} />

  return null
}

export interface PaginationSkeletonProps {
  width?: number
}

export function PaginationSkeleton({ width = 27 }: PaginationSkeletonProps) {
  return (
    <div className="gap-sm flex flex-row items-center justify-center">
      <Skeleton className="size-8 rounded-full" />
      <Skeleton className="h-4.5 rounded-[20px]" style={{ width }} />
      <Skeleton className="size-8 rounded-full" />
    </div>
  )
}
